// Single source of truth for the words in ChapterDirectorPlaybook (portal DOM) and
// ChapterDirectorPlaybookPDF (print). Rendered by ../documents/DocContent.jsx and
// ../pdf/PdfContent.jsx — the block shape is documented in DocContent.jsx.
// pdf* fields and the dom:/pdf: switches mark where the print copy
// deliberately diverges from the portal copy.

export const docTitle = "GNF Chapter Director playbook";

export const cover = {
  eyebrow: "Admin · 06",
  number: "06",
  title: "Chapter Director playbook",
  summary: "Every tool the portal hands Chapter Directors. Inviting LPs, running the review, assigning winners, publishing them live, and the rhythm to use them.",
  accent: "grape",
};

export const blocks = [
  {
    type: "lede",
    text: "Running a chapter is mostly about keeping momentum. This is your admin tour. Every tool the portal hands Chapter Directors and the rhythm for using them.",
  },
  {
    type: "heading",
    level: 1,
    text: "What a Chapter Director sees that others don't",
    pdfText: "What a Chapter Director sees",
  },
  {
    type: "p",
    runs: [
      "When your account has the ",
      { code: "chapter_director" },
      " role, the sidebar unlocks an ",
      { b: "Admin" },
      " section. Everything inside is chapter-scoped to you automatically. You can't accidentally edit another chapter's data. SuperAdmins see the same tools across all chapters.",
    ],
    pdfRuns: [
      "When your account has the chapter_director role, the sidebar unlocks an Admin section. Everything inside is chapter-scoped to you automatically. You can't accidentally edit another chapter's data. SuperAdmins see the same tools across all chapters.",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ b: "Reviews." }, " The group view of every LP's ratings."] },
      { runs: [{ b: "Grant awards." }, " Mark winners, log ad hoc awards, fill in their public info."] },
      { runs: [{ b: "Users." }, " Manage your chapter's LPs."] },
      { runs: [{ b: "Create user." }, " Invite new LPs."] },
      { runs: [{ b: "Chapter." }, " Edit your chapter's page content."] },
    ],
  },
  { type: "heading", level: 1, text: "Inviting new LPs" },
  {
    type: "p",
    runs: [
      "Head to the ",
      { b: "Create user" },
      " tab. Fill in the form (email, full name, role, usually LP, and join date) and submit. The portal sends a magic-link invite email; the new LP clicks it, sets a password, and lands in the portal with your chapter already assigned.",
    ],
    pdfRuns: [
      "Head to the Create user tab. Fill in the form (email, full name, role, usually LP, and join date) and submit. The portal sends a magic-link invite email; the new LP clicks it, sets a password, and lands in the portal with your chapter already assigned.",
    ],
  },
  {
    type: "callout",
    label: "Payment and access",
    runs: [
      "The invite itself is free to send. LPs typically pay via the Stripe renewal link (annual or semi-annual) either just before or just after you create their account, whichever's cleanest for them. If someone goes quiet after the invite, a Slack DM is usually all it takes.",
    ],
  },
  { type: "heading", level: 2, text: "Who to make a second Chapter Director" },
  {
    type: "p",
    runs: [
      "Most chapters eventually add a co-director. Pick someone who (a) shows up, (b) is comfortable running a meeting, and (c) would happily take over if you needed a month off. Promote them by editing their user and changing role to Chapter Director.",
    ],
  },
  { type: "heading", level: 1, text: "The quarterly meeting is the work", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Your most important tool isn't in the portal. It's the quarterly meeting itself. In-person, around a table, with the Reviews tab up on a screen. That's where ratings become conversations, and conversations become winners.",
    ],
  },
  {
    type: "p",
    runs: [
      { b: "We strongly prefer in-person." },
      " Something about being in the same room changes how honestly people champion a pitch they love, or challenge one the group is ready to dismiss. Virtual works as a fallback when your LPs are spread across a metro, but it should be the exception.",
    ],
    pdfRuns: [
      "We strongly prefer in-person. Something about being in the same room changes how honestly people champion a pitch they love, or challenge one the group is ready to dismiss. Virtual works as a fallback when your LPs are spread across a metro, but it should be the exception.",
    ],
  },
  { type: "heading", level: 2, text: "Running the meeting from the Reviews tab" },
  {
    type: "p",
    runs: [
      "Open the ",
      { b: "Reviews" },
      " tab in the admin panel, put it on a screen, and use it to drive the discussion:",
    ],
    pdfRuns: [
      "Open the Reviews tab in the admin panel, put it on a screen, and use it to drive the discussion:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Sort by weighted score" },
          " to surface the pitches your chapter collectively loved.",
        ],
      },
      {
        runs: [
          { b: "Sort by average score" },
          " for a different cut. Sometimes the top of this list surprises you.",
        ],
      },
      {
        runs: [
          { b: "Filter to Favorites only" },
          " to set your initial finalist pool. Widen to Considerations if no clear winner emerges.",
        ],
      },
      {
        runs: [
          { b: "Read the team notes aloud." },
          " Each note is attributed to the LP who wrote it. Use that to draw them out if they haven't spoken up yet.",
        ],
      },
    ],
  },
  {
    type: "p",
    runs: [
      "You're not there to rubber-stamp the top of the list. You're there to host a real discussion. The best meetings end with a winner nobody expected at the start. That's the group working as intended.",
    ],
  },
  {
    type: "callout",
    label: "You see names here",
    runs: [
      "On the regular ",
      { b: "Review pitches" },
      " tab, team notes are anonymized as \"LP 1\", \"LP 2\" for everyone, both LPs and Chapter Directors alike. In this admin ",
      { b: "Reviews" },
      " tab they're attributed by name, which is part of why it's the right place to run the meeting from.",
    ],
    pdfRuns: [
      "On the regular Review pitches tab, team notes are anonymized as \"LP 1\", \"LP 2\" for everyone, both LPs and Chapter Directors alike. In this admin Reviews tab they're attributed by name, which is part of why it's the right place to run the meeting from.",
    ],
  },
  { type: "heading", level: 2, text: "Shortlisting as you go", pdf: false },
  {
    type: "p",
    runs: [
      "Every pitch card in the admin Reviews tab has a ",
      { b: "Shortlist" },
      " button. As the conversation moves and a pitch earns real interest from the room (someone champions it, or the group pauses on it), hit Shortlist. It's a one-click bookmark shared across everyone with admin access.",
    ],
    pdf: false,
  },
  {
    type: "p",
    runs: [
      "When you've worked through the list, switch the rating filter to ",
      { b: "Shortlisted only" },
      ". Now you're looking at just the pitches the group wanted to come back to. Run the final round of discussion from that subset and pick your winner from there.",
    ],
    pdf: false,
  },
  {
    type: "fine",
    runs: [
      "Shortlist is persistent. It stays on the pitch across sessions. Clear it by tapping the button again if you change your mind. A co-director on the same pitch sees the same shortlist in real-ish time.",
    ],
  },
  { type: "heading", level: 2, text: "Taking notes during the discussion", pdf: false },
  {
    type: "p",
    runs: [
      "When you expand a pitch in the admin Reviews tab, below the LP Review Comments you'll find ",
      { b: "Admin Discussion Notes" },
      ". This is where follow-ups, open questions, and memorable moments from the conversation live. Use it the way the LPs use their private notes on the review form, except here anyone with admin access can add a note, and every note is attributed to the admin who wrote it.",
    ],
    pdf: false,
  },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Capture follow-ups." },
          " \"Need to verify the revenue claim.\" \"Circle back on their brick-and-mortar plans.\" Notes keep context you'd otherwise lose by the next meeting.",
        ],
      },
      {
        runs: [
          { b: "Record the room's take." },
          " If a pitch swung the group in an unexpected direction, write it down. It's material for next quarter's recruiting and a paper trail for funders.",
        ],
      },
      {
        runs: [
          { b: "Split the work with a co-director." },
          " One of you facilitates, the other types. Each note lands under its real author so you can see who captured what.",
        ],
      },
    ],
    pdf: false,
  },
  {
    type: "fine",
    runs: [
      "LPs never see admin discussion notes. Only accounts with ",
      { code: "chapter_director" },
      " or ",
      { code: "superAdmin" },
      " access do. You can edit or delete your own notes; superAdmins can manage any.",
    ],
  },
  { type: "heading", level: 1, text: "Scheduling the meeting", pdfBreak: true },
  { type: "p", runs: ["A few small habits save you from the \"nobody can make it\" spiral:"] },
  {
    type: "list",
    items: [
      {
        runs: [
          { b: "Book the date a couple of weeks out." },
          " Far enough ahead that busy calendars have room; close enough that it stays on people's radar. More than a month out and you'll lose momentum.",
        ],
      },
      {
        runs: [
          { b: "Pick a real venue." },
          " A dinner at someone's house, the back room at a local spot, a co-working lounge after hours. Anywhere that feels like a dinner party, not a meeting.",
        ],
      },
      {
        runs: [
          { b: "Send a reminder one week before." },
          " Include the portal link so LPs can finish their reviews and arrive prepped.",
        ],
      },
      {
        runs: [
          { b: "Share the prep docs." },
          " The ",
          { i: "Reviewing Pitches" },
          " guide in the Resources tab has an Export PDF button. Attach it to the reminder for any LP who wants a refresher.",
        ],
      },
    ],
  },
  { type: "heading", level: 1, text: "Assigning winners", pdfBreak: true },
  {
    type: "p",
    runs: [
      "Winners get picked at the quarterly meeting. The portal doesn't run the vote for you. Once the group has decided, here's how to make it official:",
    ],
  },
  {
    type: "list",
    items: [
      {
        runs: ["Go to the ", { b: "Grant awards" }, " tab."],
        pdfRuns: ["Go to the Grant awards tab."],
      },
      {
        runs: [
          "Find the winning pitch and toggle the ",
          { b: "Winner" },
          " flag on. The grantee is added to the tab marked ",
          { b: "Hidden" },
          "; they're not yet visible on the public site.",
        ],
        pdfRuns: [
          "Find the winning pitch and toggle the Winner flag on. The grantee is added to the tab marked Hidden; they're not yet visible on the public site.",
        ],
      },
      {
        runs: ["Fill in (or edit) their public fields:"],
        sub: [
          {
            runs: [
              { b: "About." },
              " A short paragraph about the business. The ",
              { b: "Generate with AI" },
              " button drafts one from the application; edit before saving.",
            ],
          },
          { runs: [{ b: "Website." }, " Their URL."] },
          {
            runs: [
              { b: "Pitch Photo." },
              " Upload a portrait or product shot. Saves to Firebase Storage immediately on upload.",
            ],
          },
        ],
        pdfRuns: [
          "Fill in About (use Generate with AI for a draft, then edit), Website, and upload a Pitch Photo. Photos save to storage on upload; everything else saves when you click Save.",
        ],
      },
      {
        runs: ["Click ", { b: "Save All Changes" }, "."],
        pdfRuns: ["Click Save All Changes."],
      },
      {
        runs: [
          "When you're ready to announce, flip the ",
          { b: "Live on website" },
          " switch on the winner's card. The grantee appears on the public chapter page, the Founder Map, and the Neighborhood Navigator immediately.",
        ],
        pdfRuns: [
          "When you're ready to announce, flip the Live on website switch on the winner's card. The grantee appears on the public chapter page, the Founder Map, and the Neighborhood Navigator immediately.",
        ],
      },
    ],
    ordered: true,
  },
  { type: "heading", level: 2, text: "What \"going live\" actually means" },
  {
    type: "p",
    runs: [
      "Two flags govern visibility. ",
      { code: "isWinner" },
      " marks the pitch as a grant recipient (used by your Statistics tab and internal totals).",
      { code: "winnerPublished" },
      " is the public switch: until you flip",
      { b: "Live on website" },
      " on, the grantee is hidden everywhere a member of the public would see them. New winners default to hidden so you can stage About, Website, and Photo without anything leaking before announcement day. One exception: the dollar and grant-count totals on the landing and chapter pages include hidden winners, because the money is awarded either way. Nothing identifying appears until you go live.",
    ],
    pdfRuns: [
      "Two flags govern visibility. isWinner marks the pitch as a grant recipient (used by your Statistics tab and internal totals). winnerPublished is the public switch: until you flip Live on website on, the grantee is hidden everywhere a member of the public would see them. New winners default to hidden so you can stage About, Website, and Photo without anything leaking before announcement day. One exception: the dollar and grant-count totals on the landing and chapter pages include hidden winners, because the money is awarded either way. Nothing identifying appears until you go live.",
    ],
  },
  {
    type: "callout",
    label: "Announcement order matters",
    runs: [
      "Tell the winner first (a call, ideally), then flip ",
      { b: "Live on website" },
      " on, then post to Instagram / Slack / email. The public page updates the moment you flip the switch. Don't let a founder find out they won from a social post.",
    ],
    pdfRuns: [
      "Tell the winner first (a call, ideally), then flip Live on website on, then post to Instagram / Slack / email. The public page updates the moment you flip the switch. Don't let a founder find out they won from a social post.",
    ],
  },
  { type: "heading", level: 2, text: "Un-publishing a winner" },
  {
    type: "p",
    runs: [
      "Flipping ",
      { b: "Live on website" },
      " off hides the grantee again, useful if you spot a typo or need to delay an announcement. Toggling it doesn't affect their winner status, the award amount, or any totals, internal or public; it only controls whether their profile is visible.",
    ],
    pdfRuns: [
      "Flipping Live on website off hides the grantee again, useful if you spot a typo or need to delay an announcement. Toggling it doesn't affect their winner status, the award amount, or any totals, internal or public; it only controls whether their profile is visible.",
    ],
  },
  { type: "heading", level: 2, text: "Editing a past winner" },
  {
    type: "p",
    runs: [
      "Returning to the Grant awards tab later lets you update any winner's About, website, or photo. Useful when a grantee launches, rebrands, or sends better photography. The changes show up on the public chapter page instantly.",
    ],
    pdfRuns: [
      "Returning to the Grant awards tab later lets you update any winner's About, website, or photo. Useful when a grantee launches, rebrands, or sends better photography. Changes show up on the public chapter page instantly.",
    ],
  },
  { type: "heading", level: 2, text: "Logging an award made outside the app" },
  {
    type: "p",
    runs: [
      "Some grants don't come through the application process: live pitch competitions, school competitions, partner events. Log these from the ",
      { b: "Grant awards" },
      " tab with the ",
      { b: "Log grant award" },
      " button. Enter the business, founder, amount, and the quarter the grant was issued. Logged awards count toward your chapter totals and GNF-wide totals just like application winners, and they start hidden: add a photo and About, then flip ",
      { b: "Live on website" },
      " to publish. They never appear in the review pipeline, so your LPs won't be asked to review them.",
    ],
    pdfRuns: [
      "Some grants don't come through the application process: live pitch competitions, school competitions, partner events. Log these from the Grant awards tab with the Log grant award button. Enter the business, founder, amount, and the quarter the grant was issued. Logged awards count toward your chapter totals and GNF-wide totals just like application winners, and they start hidden: add a photo and About, then flip Live on website to publish. They never appear in the review pipeline, so your LPs won't be asked to review them.",
    ],
  },
  { type: "heading", level: 1, text: "User management", pdfBreak: true },
  {
    type: "p",
    runs: ["The ", { b: "Users" }, " tab lists everyone in your chapter. From here you can:"],
    pdfRuns: ["The Users tab lists everyone in your chapter. From here you can:"],
  },
  {
    type: "list",
    items: [
      { runs: ["Edit an LP's name, email, or role."] },
      { runs: ["Trigger a password reset email (the LP handles the rest)."] },
      { runs: ["Deactivate a lapsed LP (or reactivate when they renew)."] },
    ],
  },
  {
    type: "p",
    runs: [
      "You cannot edit users outside your chapter. If an LP needs to move chapters (common when someone relocates), ping a SuperAdmin in the national Slack.",
    ],
  },
  { type: "heading", level: 1, text: "Editing your chapter page" },
  {
    type: "p",
    runs: [
      "The ",
      { b: "Chapter" },
      " tab lets you edit the copy and imagery that renders on your public chapter page, the landing page prospective LPs and founders see before they apply. Use this to keep the meeting schedule fresh, swap the hero image seasonally, and update the LP roster.",
    ],
    pdfRuns: [
      "The Chapter tab lets you edit the copy and imagery on your public chapter page, the landing page prospective LPs and founders see before they apply. Use it to keep the meeting schedule fresh, swap the hero image seasonally, and update the LP roster.",
    ],
  },
  {
    type: "fine",
    runs: [
      "Some chapter pages are hand-built static HTML (Denver, WNY, Central NY, Capital Region, Hudson Valley). Those are edited in code by GNF HQ, not through this tab. If you're unsure which you have, ask in the national Slack before assuming edits didn't save.",
    ],
    pullUp: true,
  },
  {
    type: "p",
    dom: false,
    runs: [
      "Note: some chapter pages (Denver, WNY, Central NY, Capital Region, Hudson Valley) are hand-built static HTML and are edited in code by GNF HQ, not through this tab. If you're unsure which you have, ask in the national Slack before assuming edits didn't save.",
    ],
  },
  { type: "heading", level: 1, text: "The quarterly rhythm", pdfBreak: true },
  {
    type: "p",
    runs: [
      "A chapter that runs on time is a chapter that stays alive. Loose template for each quarter:",
    ],
  },
  {
    type: "list",
    items: [
      { runs: [{ b: "Weeks 1 to 3:" }, " promote the chapter, funnel applications."] },
      { runs: [{ b: "Week 4:" }, " close applications. Post a deadline reminder in Slack."] },
      { runs: [{ b: "Weeks 5 to 7:" }, " LPs review async. You nudge at week 6."] },
      {
        runs: [
          { b: "Week 8:" },
          " quarterly meeting. The group reviews top finalists and votes on the winner.",
        ],
      },
      {
        runs: [
          { b: "Week 9:" },
          " notify the winner, mark them in the Grant awards tab, fill in their public info, then flip ",
          { b: "Live on website" },
          " on and post everywhere.",
        ],
        pdfRuns: [
          { b: "Week 9:" },
          " notify the winner, mark them in the Grant awards tab, fill in their public info, then flip Live on website on and post everywhere.",
        ],
      },
      {
        runs: [{ b: "Weeks 10 to 12:" }, " rest. Recruit one or two new LPs. Start the next cycle."],
      },
    ],
  },
  { type: "heading", level: 1, text: "Where to get help" },
  {
    type: "list",
    items: [
      { runs: [{ b: "hello@goodneighbor.fund" }, " for anything portal-related or sensitive."] },
      {
        runs: [
          "The ",
          { i: "Recruiting Limited Partners" },
          " and ",
          { i: "New Chapter Handbook" },
          " docs in this library cover the non-admin side of the job.",
        ],
        pdfRuns: [
          "The Recruiting Limited Partners and New Chapter Handbook docs cover the non-admin side of the job.",
        ],
      },
    ],
  },
  {
    type: "callout",
    label: "The heart of the role",
    runs: [
      "The admin panel handles the paperwork. Your actual job is hosting. Setting the table, making the toast, welcoming a new LP, celebrating a grantee. You're not managing a process. You're building a neighborhood.",
    ],
  },
];
